<!-- The tabs stand on the row's rule (their selected bar meets it); the toggle sits centred above it. -->
<div class="flex h-21 items-end border-b border-border">
  <ng-content />

  <span class="mr-8 shrink-0 self-center rounded-md" [class.bg-gray-200]="opened()">
    <app-button2
      #toggleButton
      data-test="kp-search-toggle"
      appearance="secondary-ghost"
      size="m"
      ariaLabel="Search knots, tags and connections"
      [ariaPressed]="opened()"
      [ariaExpanded]="opened()"
      [ariaControls]="opened() ? menuId : null"
      [disabled]="disabled()"
      (click)="toggle()"
    >
      <app-icon name="sapphire-search" [size]="20" />
    </app-button2>
  </span>
</div>

@if (opened()) {
  <search class="block border-b border-border px-8 py-4" (keydown.escape)="closeToToggle()">
    <div
      #field
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      class="flex min-h-16 w-full items-center gap-4 rounded-md border border-border bg-white px-4 focus-within:border-blue-600"
    >
      <app-icon name="sapphire-search" [size]="20" class="shrink-0" />

      <input
        #input
        type="text"
        data-test="kp-search-input"
        role="combobox"
        aria-label="Search related knots, tags and connections"
        aria-autocomplete="list"
        aria-haspopup="dialog"
        [attr.aria-expanded]="menuAttached()"
        [attr.aria-controls]="menuId"
        autocomplete="off"
        placeholder="Search"
        [maxLength]="queryMaxLength"
        class="min-w-0 flex-1 bg-transparent py-3 text-variant-md text-gray-950 outline-none placeholder:text-gray-500"
        [value]="text()"
        (input)="handleInput($event)"
        (keydown.enter)="addKeyword($event)"
        (keydown.arrowdown)="focusMenu($event)"
      />

      @if (text()) {
        <app-button2
          data-test="kp-search-clear"
          class="shrink-0"
          appearance="secondary-ghost"
          size="s"
          ariaLabel="Clear search text"
          (click)="clearText()"
        >
          <app-icon name="sapphire-close" [size]="16" />
        </app-button2>
      }
    </div>
  </search>

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="origin"
    [cdkConnectedOverlayOpen]="true"
    [cdkConnectedOverlayPositions]="overlayPositions"
    [cdkConnectedOverlayWidth]="width() || undefined"
    [cdkConnectedOverlayDisableClose]="true"
    (attach)="menuAttached.set(true)"
    (detach)="menuAttached.set(false)"
    (overlayOutsideClick)="handleOutsideClick($event)"
    (overlayKeydown)="handleOverlayKeydown($event)"
  >
    <div
      #menu
      [id]="menuId"
      role="dialog"
      aria-label="Related matches"
      data-test="kp-search-menu"
      class="flex max-h-200 w-full flex-col gap-4 overflow-y-auto rounded-lg bg-white p-6 shadow-lg"
      (keydown)="handleMenuKeydown($event)"
    >
      @let selected = markers();
      @if (selected.length) {
        <div data-test="kp-search-selected" class="flex items-start justify-between gap-4 border-b border-border pb-4">
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <span class="whitespace-nowrap text-sm font-semibold text-gray-800">{{ selected.length }} selected</span>

            @for (marker of selected; track marker.key) {
              @switch (marker.kind) {
                @case ('knot') {
                  <app-knot
                    data-test="kp-search-selected-chip"
                    class="min-w-0"
                    [knot]="marker.item"
                    [selected]="true"
                    [withRemove]="true"
                    [withControls]="false"
                    [dragEnabled]="false"
                    [dropEnabled]="false"
                    (onRemove)="removeMarker(marker)"
                  />
                }
                @case ('tag') {
                  <app-tag
                    data-test="kp-search-selected-chip"
                    class="min-w-0"
                    [tag]="marker.item"
                    [selected]="true"
                    [withRemove]="true"
                    [withControls]="false"
                    [dragEnabled]="false"
                    [dropEnabled]="false"
                    (onRemove)="removeMarker(marker)"
                  />
                }
                @case ('connection') {
                  <app-connection
                    data-test="kp-search-selected-chip"
                    class="min-w-0"
                    [connection]="marker.item"
                    [selected]="true"
                    [withRemove]="true"
                    [withControls]="false"
                    [dragEnabled]="false"
                    [dropEnabled]="false"
                    (onRemove)="removeMarker(marker)"
                  />
                }
                @case ('keyword') {
                  <app-kp-keyword-chip
                    data-test="kp-search-selected-chip"
                    class="min-w-0"
                    [keyword]="marker.item.name"
                    (remove)="removeMarker(marker)"
                  />
                }
              }
            }
          </div>

          <app-button2
            data-test="kp-search-selected-clear"
            class="shrink-0"
            appearance="primary-text"
            size="s"
            (click)="clearSelection()"
          >
            Clear
          </app-button2>
        </div>
      }

      @let found = matches();

      <div
        data-test="kp-search-group-knots"
        role="group"
        aria-label="Related Knots"
        class="flex flex-wrap items-center gap-2 rounded bg-primary-50 px-4 py-5"
      >
        @if (searched() && !found.knots.length) {
          <span data-test="kp-search-empty" class="w-full text-center text-sm font-semibold text-gray-800">
            No related Knots
          </span>
        } @else {
          <span class="whitespace-nowrap text-sm font-semibold text-gray-800">Related Knots:</span>
          @for (knot of found.knots; track knot.id) {
            <!-- Native button: the chip is the label, and app-button2 would add its own height and padding. -->
            <button
              type="button"
              data-menu-item
              data-test="kp-search-option"
              class="min-w-0 max-w-full cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600"
              (click)="pickKnot(knot)"
            >
              <app-knot [knot]="knot" [withControls]="false" [dragEnabled]="false" [dropEnabled]="false" />
            </button>
          }
        }
      </div>

      <div
        data-test="kp-search-group-tags"
        role="group"
        aria-label="Related Tags"
        class="flex flex-wrap items-center gap-2 rounded bg-primary-50 px-4 py-5"
      >
        @if (searched() && !found.tags.length) {
          <span data-test="kp-search-empty" class="w-full text-center text-sm font-semibold text-gray-800">
            No related Tags
          </span>
        } @else {
          <span class="whitespace-nowrap text-sm font-semibold text-gray-800">Related Tags:</span>
          @for (tag of found.tags; track tag.id) {
            <button
              type="button"
              data-menu-item
              data-test="kp-search-option"
              class="min-w-0 max-w-full cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600"
              (click)="pickTag(tag)"
            >
              <app-tag [tag]="tag" [withControls]="false" [dragEnabled]="false" [dropEnabled]="false" />
            </button>
          }
        }
      </div>

      <div
        data-test="kp-search-group-connections"
        role="group"
        aria-label="Related Connections"
        class="flex flex-wrap items-center gap-2 rounded bg-primary-50 px-4 py-5"
      >
        @if (searched() && !found.connections.length) {
          <span data-test="kp-search-empty" class="w-full text-center text-sm font-semibold text-gray-800">
            No related Connections
          </span>
        } @else {
          <span class="whitespace-nowrap text-sm font-semibold text-gray-800">Related Connections:</span>
          @for (connection of found.connections; track connection.id) {
            <button
              type="button"
              data-menu-item
              data-test="kp-search-option"
              class="min-w-0 max-w-full cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600"
              (click)="pickConnection(connection)"
            >
              <app-connection
                [connection]="connection"
                [withControls]="false"
                [dragEnabled]="false"
                [dropEnabled]="false"
              />
            </button>
          }
        }
      </div>

      @if (loading()) {
        <div data-test="kp-search-loading" class="flex justify-center">
          <app-loading-icon [size]="16" />
        </div>
      }
    </div>
  </ng-template>
}
